import type { IDataTableHandler, RowData } from '../types';

const RESERVED_EVENTS = new Set([
  'save',
  'delete',
  'selection-change',
  'focus-cell',
]);

interface HandlerOptions {
  skipConfirm?: boolean;
  confirmMessage?: string | ((row: RowData) => string);
  onSave?: (row: RowData) => Promise<RowData>;
  onSaveCompleted?: (row: RowData, result: RowData) => void;
  onDelete?: (row: RowData) => Promise<boolean>;
  onDeleteCompleted?: (row: RowData) => void;
  onSelectionChange?: (payload: any) => void;
  onCellEvent?: (event: string, payload: any) => void;
}

export function useDataTableHandler(
  options: HandlerOptions = {},
): IDataTableHandler {
  const emit = async (event: string, ...args: any[]) => {
    if (event === 'selection-change') {
      options.onSelectionChange?.(args[0]);
      return Promise.resolve(true);
    }

    if (event === 'delete') {
      const payload = args[0] || {};
      const row = payload.row;
      const skipConfirm = payload.skipConfirm;

      const runDelete = async () => {
        let result: boolean | undefined = true;
        if (options.onDelete) {
          result = await options.onDelete(row);
        }
        if (options.onDeleteCompleted) {
          options.onDeleteCompleted(row);
        }
        return result;
      };

      if (skipConfirm || options.skipConfirm) {
        return await runDelete();
      }

      const msg =
        typeof options.confirmMessage === 'function'
          ? options.confirmMessage(row)
          : options.confirmMessage ||
            'Are you sure you want to delete this row?';

      if (confirm(msg)) {
        return await runDelete();
      } else {
        throw new Error('Cancelled');
      }
    }

    if (event === 'save') {
      const { row } = args[0];
      let result: RowData = row;
      if (options.onSave) {
        result = await options.onSave(row);
      }
      if (options.onSaveCompleted) {
        options.onSaveCompleted(row, result);
      }
      return Promise.resolve(result);
    }

    if (!RESERVED_EVENTS.has(event) && options.onCellEvent) {
      options.onCellEvent(event, args[0]);
    }

    return Promise.resolve(true);
  };

  return { emit };
}
